import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Grid, 
  List, 
  Filter, 
  Search, 
  X, 
  ChevronDown,
  ShoppingCart
} from 'lucide-react';
import { useQuery } from 'react-query';
import { productsAPI, categoriesAPI } from '../services/api';
import ProductCard from '../components/ui/ProductCard';
import LocalNotification from '../components/ui/LocalNotification';
import InlineEditor from '../components/ui/InlineEditor';
import { useSiteSettings } from '../hooks/useSiteSettings';

const ProductsPage = () => {
  const { t } = useLanguage();
  const { getSetting } = useSiteSettings();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [showFilters, setShowFilters] = useState(false);
  const [notifications, setNotifications] = useState({});

  // Fetch products and categories
  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery(
    ['products', { 
      category: selectedCategory || undefined, 
      limit: 100, 
      search: searchTerm || undefined 
    }],
    async () => {
      const response = await productsAPI.getAll({ 
        category: selectedCategory || undefined, 
        limit: 100, 
        search: searchTerm || undefined 
      });
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
    }
  );

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery(
    ['categories'],
    async () => {
      const response = await categoriesAPI.getAll();
      return response.data.categories || [];
    }
  );

  const products = productsData?.products || [];
  const categories = categoriesData || [];

  // Debug logs
  console.log('Selected category:', selectedCategory);
  console.log('Products fetched:', products.length);
  console.log('API call params:', { 
    category: selectedCategory || undefined, 
    limit: 100, 
    search: searchTerm || undefined 
  });

  // Since filtering is done server-side via API, we only need to sort client-side
  const filteredProducts = products;

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return (a.sale_price || a.price) - (b.sale_price || b.price);
      case 'price-high':
        return (b.sale_price || b.price) - (a.sale_price || a.price);
      case 'name':
        return a.name_fr.localeCompare(b.name_fr);
      case 'newest':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage);

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle sort change
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategory('');
    setSearchTerm('');
    setSortBy('newest');
    setCurrentPage(1);
  };

  // Show notification
  const showNotification = (productId, message, type = 'success') => {
    setNotifications(prev => ({
      ...prev,
      [productId]: { show: true, message, type }
    }));
  };

  if (productsLoading || categoriesLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-iranian-500 mx-auto"></div>
          <p className="text-white mt-4">Chargement des produits...</p>
        </div>
      </div>
    );
  }

  if (productsError) {
    return (
      <div className="min-h-screen bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Erreur de chargement</h1>
          <p className="text-gray-300 mb-8">Impossible de charger les produits pour le moment.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-iranian-500 hover:bg-iranian-600 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-12 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            <InlineEditor settingKey="products_page_title_fr" defaultValue="Notre Collection de Bijoux">
              {getSetting('products_page_title_fr', 'Notre Collection de Bijoux')}
            </InlineEditor>
          </h1>
        </div>
        
        {/* Filters and Search Bar */}
        <div className="bg-gray-800 rounded-xl p-4 sm:p-6 mb-8">
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Rechercher un bijou..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
              {/* Category and Sort Filters */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="px-3 sm:px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-iranian-500 text-sm sm:text-base flex-1 sm:flex-none"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name_fr}
                    </option>
                  ))}
                </select>

                <select
                  value={sortBy}
                  onChange={handleSortChange}
                  className="px-3 sm:px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-iranian-500 text-sm sm:text-base flex-1 sm:flex-none"
                >
                  <option value="newest">Plus récents</option>
                  <option value="price-low">Prix croissant</option>
                  <option value="price-high">Prix décroissant</option>
                  <option value="name">Nom A-Z</option>
                </select>
              </div>

              {/* View Mode Toggle and Clear Filters */}
              <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                {/* View Mode Toggle */}
                <div className="flex bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'grid' 
                        ? 'bg-iranian-500 text-white' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Grid className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'list' 
                        ? 'bg-iranian-500 text-white' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <List className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </div>

                {/* Clear Filters */}
                {(selectedCategory || searchTerm || sortBy !== 'newest') && (
                  <button
                    onClick={clearFilters}
                    className="px-3 sm:px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors text-sm sm:text-base"
                  >
                    Effacer
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {(selectedCategory || searchTerm) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedCategory && (
                <span className="inline-flex items-center px-2 sm:px-3 py-1 bg-iranian-500 text-white text-xs sm:text-sm rounded-full">
                  <span className="hidden sm:inline">Catégorie: </span>{categories.find(c => c.id === parseInt(selectedCategory))?.name_fr}
                  <button
                    onClick={() => setSelectedCategory('')}
                    className="ml-1 sm:ml-2 text-iranian-200 hover:text-white"
                  >
                    ×
                  </button>
                </span>
              )}
              {searchTerm && (
                <span className="inline-flex items-center px-2 sm:px-3 py-1 bg-blue-500 text-white text-xs sm:text-sm rounded-full">
                  <span className="hidden sm:inline">Recherche: </span>"{searchTerm}"
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-1 sm:ml-2 text-blue-200 hover:text-white"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results Summary */}
        <div className="flex justify-between items-center mb-6">
            <p className="text-gray-300">
            {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
            {selectedCategory && ` dans ${categories.find(c => c.id === parseInt(selectedCategory))?.name_fr}`}
          </p>
          {searchTerm && (
            <p className="text-gray-400 text-sm">
              Résultats pour "{searchTerm}"
            </p>
          )}
        </div>

        {/* Products Grid/List */}
        {paginatedProducts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Aucun produit trouvé</h3>
            <p className="text-gray-400 mb-6">
              Essayez de modifier vos critères de recherche ou de sélectionner une autre catégorie.
            </p>
            <button
              onClick={clearFilters}
              className="bg-iranian-500 hover:bg-iranian-600 text-white px-6 py-3 rounded-lg transition-colors"
            >
              Effacer tous les filtres
            </button>
          </div>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {paginatedProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className={`
                      ${index % 4 !== 3 ? 'border-r border-[rgb(128,128,128)]' : ''} 
                      ${index < paginatedProducts.length - 4 ? 'border-b border-[rgb(128,128,128)]' : ''}
                    `}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="space-y-4">
                {paginatedProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-gray-800 rounded-xl p-4 sm:p-6 hover:bg-gray-750 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                      {/* Product Image */}
                      <div className="flex-shrink-0 mx-auto sm:mx-0">
                        <img
                          src={product.main_photo || '/placeholder-product.svg'}
                          alt={product.name_fr}
                          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = '/placeholder-product.svg';
                          }}
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0 text-center sm:text-left">
                        <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">
                          {product.name_fr}
                        </h3>
                        <p className="text-gray-400 text-sm mb-2">
                          {product.category_name || 'Collection Perse'}
                        </p>
                        <p className="text-gray-300 text-sm line-clamp-2 hidden sm:block">
                          {product.description_fr}
                        </p>
                        
                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                          {product.material && (
                            <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">
                              {product.material}
                            </span>
                          )}
                          {product.gemstone && (
                            <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">
                              {product.gemstone}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Price and Actions */}
                      <div className="flex-shrink-0 text-center sm:text-right">
                        <div className="text-xl sm:text-2xl font-bold text-iranian-400 mb-3">
                          {product.sale_price ? (
                            <>
                              <span className="text-iranian-400">
                                {new Intl.NumberFormat('fr-FR', {
                                  style: 'currency',
                                  currency: 'EUR'
                                }).format(product.sale_price)}
                              </span>
                              <span className="text-gray-500 line-through ml-2 text-sm sm:text-lg">
                                {new Intl.NumberFormat('fr-FR', {
                                  style: 'currency',
                                  currency: 'EUR'
                                }).format(product.price)}
                              </span>
                            </>
                          ) : (
                            new Intl.NumberFormat('fr-FR', {
                              style: 'currency',
                              currency: 'EUR'
                            }).format(product.price)
                          )}
                        </div>
                        
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                          <button className="bg-iranian-500 hover:bg-iranian-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 text-sm">
                            <ShoppingCart className="h-4 w-4" />
                            <span>Ajouter</span>
                          </button>
                          <button className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors text-sm">
                            Voir détails
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-12">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded-lg transition-colors"
                  >
                    Précédent
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 rounded-lg transition-colors ${
                        currentPage === page
                          ? 'bg-iranian-500 text-white'
                          : 'bg-gray-700 hover:bg-gray-600 text-white'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded-lg transition-colors"
                  >
                    Suivant
                  </button>
          </div>
        </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage; 